import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Bug, Megaphone, Star, ClipboardText, CloudArrowUp, X, CheckCircle, type Icon,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { feedbackApi, getErrorMessage } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import ReportsPanel from './ReportsPanel'
import { cn } from '@/lib/utils'
import {
  inputClass, inputStyle, textareaClass, labelClass, labelStyle,
  errorBorderClass, infoBannerClass, bannerTextStyle, cardChromeClass,
} from '@/lib/styleKit'
import type {
  FeedbackType, FeedbackSubtype, FeedbackModule, BugSeverity, FeatureImportance,
  BugFormFields, ImproveExistingFormFields, NewFeatureFormFields, RatingFormFields,
  FeedbackContext, FeedbackSubmitResult, FeedbackMineItem,
} from '@/types/feedback'

const MODULE_OPTIONS: { value: FeedbackModule; label: string }[] = [
  { value: 'Contacts', label: 'Contacts' },
  { value: 'Workflows', label: 'Workflows' },
  { value: 'Templates', label: 'Templates' },
  { value: 'Materials', label: 'Materials' },
  { value: 'Campaigns', label: 'Campaigns' },
  { value: 'Analytics', label: 'Analytics' },
  { value: 'Inbox', label: 'Inbox' },
  { value: 'Settings', label: 'Settings' },
]

const SEVERITY_OPTIONS: { value: BugSeverity; label: string }[] = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
  { value: 'Blocking', label: 'Blocking' },
]

const IMPORTANCE_OPTIONS: { value: FeatureImportance; label: string }[] = [
  { value: 'Nice to have', label: 'Nice to have' },
  { value: 'Important', label: 'Important' },
  { value: 'Critical', label: 'Critical' },
]

const RATING_LABELS: Record<number, string> = {
  1: 'Poor', 2: 'Fair', 3: 'Average', 4: 'Good', 5: 'Excellent',
}

// Current route -> module list, so "Where it happened" opens pre-filled to
// wherever the user actually was (the user can still change it). Settings
// itself, and anything unmapped, falls back to no pre-fill.
const ROUTE_MODULE: { prefix: string; module: FeedbackModule }[] = [
  { prefix: '/contacts', module: 'Contacts' },
  { prefix: '/workflows', module: 'Workflows' },
  { prefix: '/templates', module: 'Templates' },
  { prefix: '/materials', module: 'Materials' },
  { prefix: '/campaigns', module: 'Campaigns' },
  { prefix: '/analytics', module: 'Analytics' },
  { prefix: '/inbox', module: 'Inbox' },
  { prefix: '/settings', module: 'Settings' },
]

function moduleForPath(pathname: string): FeedbackModule | '' {
  return ROUTE_MODULE.find((r) => pathname.startsWith(r.prefix))?.module ?? ''
}

const EMPTY_BUG: BugFormFields = { title: '', module: '', severity: 'Medium', description: '', steps_to_reproduce: '' }
const EMPTY_IMPROVE: ImproveExistingFormFields = { area: '', suggestion: '' }
const EMPTY_NEW_FEATURE: NewFeatureFormFields = { area: '', idea: '', problem_solved: '', importance: '' }
const EMPTY_RATING: RatingFormFields = { rating: 0, reason: '' }

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024
const ALLOWED_ATTACHMENT_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.pdf']

const FeedbackSupportSection: React.FC = () => {
  const email = useAuthStore((s) => s.user?.email) ?? ''
  const location = useLocation()

  // 'reports' is a view, not a submission type: the fourth card shows the
  // user's own reports and their status instead of a form.
  const [type, setType] = useState<FeedbackType | 'reports'>('bug')
  const [reports, setReports] = useState<FeedbackMineItem[] | null>(null)
  const [reportsFailed, setReportsFailed] = useState(false)
  const [unseenReports, setUnseenReports] = useState(0)
  const [subtype, setSubtype] = useState<FeedbackSubtype>('improve_existing')
  const [bug, setBug] = useState<BugFormFields>({ ...EMPTY_BUG, module: moduleForPath(location.pathname) })
  const [improve, setImprove] = useState<ImproveExistingFormFields>({ ...EMPTY_IMPROVE, area: moduleForPath(location.pathname) })
  const [newFeature, setNewFeature] = useState<NewFeatureFormFields>({ ...EMPTY_NEW_FEATURE, area: moduleForPath(location.pathname) })
  const [rating, setRating] = useState<RatingFormFields>(EMPTY_RATING)
  const [hoveredStar, setHoveredStar] = useState(0)
  const [attachment, setAttachment] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [result, setResult] = useState<FeedbackSubmitResult | null>(null)

  const clearError = (field: string) => setErrors((prev) => {
    if (!prev[field]) return prev
    const next = { ...prev }
    delete next[field]
    return next
  })

  const switchType = (next: FeedbackType | 'reports') => {
    setType(next)
    setErrors({})
    setSubmitError(null)
  }
  const switchSubtype = (next: FeedbackSubtype) => {
    setSubtype(next)
    setErrors({})
    setSubmitError(null)
  }

  const validateAndSetAttachment = (file: File) => {
    const lower = file.name.toLowerCase()
    if (!ALLOWED_ATTACHMENT_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
      setErrors((prev) => ({ ...prev, attachment: 'Only PNG, JPG, or PDF files are supported.' }))
      return
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setErrors((prev) => ({ ...prev, attachment: 'Attachment must be 10 MB or smaller.' }))
      return
    }
    clearError('attachment')
    setAttachment(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false)
    const dropped = e.dataTransfer.files[0]
    if (dropped) validateAndSetAttachment(dropped)
  }
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (selected) validateAndSetAttachment(selected)
  }

  const loadReports = useCallback(() => {
    feedbackApi
      .mine()
      .then((res) => {
        setReports(res.data.items)
        setUnseenReports(res.data.unseen_count)
        setReportsFailed(false)
      })
      .catch(() => setReportsFailed(true))
  }, [])

  useEffect(() => {
    loadReports()
  }, [loadReports])

  useEffect(() => {
    if (type !== 'reports' || unseenReports === 0) return
    // Opening the card is "I've seen these": clear the badge for every device.
    // The row markers stay until the next load so the user can still tell which moved.
    setUnseenReports(0)
    feedbackApi.markReportsSeen().catch(() => {})
  }, [type, unseenReports])

  const resetFields = () => {
    loadReports()
    setBug({ ...EMPTY_BUG, module: moduleForPath(location.pathname) })
    setImprove({ ...EMPTY_IMPROVE, area: moduleForPath(location.pathname) })
    setNewFeature({ ...EMPTY_NEW_FEATURE, area: moduleForPath(location.pathname) })
    setRating(EMPTY_RATING)
    setHoveredStar(0)
    setAttachment(null)
    setErrors({})
    setSubmitError(null)
    setResult(null)
  }

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {}
    if (type === 'bug') {
      if (!bug.title.trim()) next.title = 'What went wrong is required.'
      if (!bug.module) next.module = 'Where it happened is required.'
      if (!bug.description.trim()) next.description = 'Description is required.'
    } else if (type === 'feedback' && subtype === 'improve_existing') {
      if (!improve.area) next.area = 'Which area is required.'
      if (!improve.suggestion.trim()) next.suggestion = 'Your suggestion is required.'
    } else if (type === 'feedback' && subtype === 'new_feature') {
      if (!newFeature.idea.trim()) next.idea = 'The idea is required.'
    } else if (type === 'rating') {
      if (rating.rating < 1) next.rating = 'Pick a star rating.'
    }
    return next
  }

  const buildPayload = (): Record<string, unknown> => {
    if (type === 'bug') {
      return {
        title: bug.title.trim(),
        module: bug.module,
        severity: bug.severity,
        description: bug.description.trim(),
        ...(bug.steps_to_reproduce.trim() ? { steps_to_reproduce: bug.steps_to_reproduce.trim() } : {}),
      }
    }
    if (type === 'feedback' && subtype === 'improve_existing') {
      return { area: improve.area, suggestion: improve.suggestion.trim() }
    }
    if (type === 'feedback') {
      return {
        ...(newFeature.area ? { area: newFeature.area } : {}),
        idea: newFeature.idea.trim(),
        ...(newFeature.problem_solved.trim() ? { problem_solved: newFeature.problem_solved.trim() } : {}),
        ...(newFeature.importance ? { importance: newFeature.importance } : {}),
      }
    }
    return {
      rating: rating.rating,
      ...(rating.reason.trim() ? { reason: rating.reason.trim() } : {}),
    }
  }

  const buildContext = (): FeedbackContext => ({
    user_agent: navigator.userAgent,
    app_version: import.meta.env.VITE_SENTRY_RELEASE || 'dev',
    url: location.pathname,
    timestamp: new Date().toISOString(),
  })

  const handleSubmit = async () => {
    const fieldErrors = validate()
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors)
      return
    }
    setErrors({})
    setSubmitError(null)
    setSubmitting(true)
    try {
      const formData = new FormData()
      formData.append('type', type)
      if (type === 'feedback') formData.append('subtype', subtype)
      formData.append('payload', JSON.stringify(buildPayload()))
      if (type === 'bug') formData.append('context', JSON.stringify(buildContext()))
      if (attachment) formData.append('attachment', attachment)

      const res = await feedbackApi.submit(formData)
      setResult(res.data)
    } catch (err) {
      setSubmitError(getErrorMessage(err, 'Could not send this. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  const submitLabel = type === 'bug' ? 'Submit report' : type === 'feedback' ? 'Send feedback' : 'Submit rating'

  if (result) {
    return (
      <div className="space-y-6">
        <Header />
        <div className={cn(cardChromeClass, 'rounded-lg border p-10 text-center')}>
          <CheckCircle className="h-10 w-10 text-emerald-600 dark:text-emerald-400 mx-auto mb-4" weight="fill" />
          <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '16px', fontWeight: 590 }}>
            Thanks, we've got it.
          </p>
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-1.5" style={bannerTextStyle}>
            We'll follow up at {email}.
          </p>
          {result.reference_code && (
            <p className="mt-3 font-mono text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px' }}>
              Reference: {result.reference_code}
            </p>
          )}
          <Button variant="outline" size="sm" className="mt-6" onClick={resetFields}>
            Submit another
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Header />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" role="group" aria-label="What's this about?">
        <TypeCard
          label="Report a bug" icon={Bug} active={type === 'bug'}
          onClick={() => switchType('bug')}
        />
        <TypeCard
          label="Share feedback" icon={Megaphone} active={type === 'feedback'}
          onClick={() => switchType('feedback')}
        />
        <TypeCard
          label="Rate our app" icon={Star} active={type === 'rating'}
          onClick={() => switchType('rating')}
        />
        <TypeCard
          label="Reports" icon={ClipboardText} active={type === 'reports'}
          badge={unseenReports}
          onClick={() => switchType('reports')}
        />
      </div>

      {type === 'reports' ? (
        <div className={cn(cardChromeClass, 'rounded-lg border p-5')}>
          <ReportsPanel items={reports} failed={reportsFailed} />
        </div>
      ) : (
      <div className={cn(cardChromeClass, 'rounded-lg border p-5 space-y-4')}>
        {type === 'bug' && (
          <>
            <Field label="What went wrong" error={errors.title}>
              <input
                aria-label="What went wrong"
                value={bug.title}
                onChange={(e) => { setBug({ ...bug, title: e.target.value }); clearError('title') }}
                placeholder="Short summary of the problem"
                className={cn(inputClass, errors.title && errorBorderClass)}
                style={inputStyle}
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Where it happened" error={errors.module}>
                <Select
                  value={bug.module}
                  onChange={(v) => { setBug({ ...bug, module: v as FeedbackModule }); clearError('module') }}
                  options={MODULE_OPTIONS}
                  ariaLabel="Where it happened"
                  className="w-full"
                />
              </Field>
              <Field label="Severity">
                <Select
                  value={bug.severity}
                  onChange={(v) => setBug({ ...bug, severity: v as BugSeverity })}
                  options={SEVERITY_OPTIONS}
                  ariaLabel="Severity"
                  className="w-full"
                />
              </Field>
            </div>
            <Field label="Description" error={errors.description}>
              <textarea
                aria-label="Description"
                value={bug.description}
                onChange={(e) => { setBug({ ...bug, description: e.target.value }); clearError('description') }}
                placeholder="What happened? What did you expect instead?"
                rows={3}
                className={cn(textareaClass, errors.description && errorBorderClass)}
                style={inputStyle}
              />
            </Field>
            <Field label="Steps to reproduce (optional)">
              <textarea
                aria-label="Steps to reproduce"
                value={bug.steps_to_reproduce}
                onChange={(e) => setBug({ ...bug, steps_to_reproduce: e.target.value })}
                placeholder="1. … 2. … 3. …"
                rows={2}
                className={textareaClass}
                style={inputStyle}
              />
            </Field>
            <AttachmentField
              attachment={attachment} isDragging={isDragging} error={errors.attachment}
              fileInputRef={fileInputRef}
              onDrop={handleDrop}
              onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true) }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false) }}
              onFileSelect={handleFileSelect}
              onRemove={() => setAttachment(null)}
            />
            <div className={infoBannerClass}>
              <p style={bannerTextStyle}>
                We'll include your browser, app version, and the page you're on to help us debug.
                No passwords or email contents are sent.
              </p>
            </div>
          </>
        )}

        {type === 'feedback' && (
          <>
            <div role="group" aria-label="What kind of feedback?" className="flex gap-2">
              <PillToggle label="Improve an existing feature" active={subtype === 'improve_existing'} onClick={() => switchSubtype('improve_existing')} />
              <PillToggle label="Suggest a new feature" active={subtype === 'new_feature'} onClick={() => switchSubtype('new_feature')} />
            </div>
            {subtype === 'improve_existing' ? (
              <>
                <Field label="Which area" error={errors.area}>
                  <Select
                    value={improve.area}
                    onChange={(v) => { setImprove({ ...improve, area: v as FeedbackModule }); clearError('area') }}
                    options={MODULE_OPTIONS}
                    ariaLabel="Which area"
                    className="w-full"
                  />
                </Field>
                <Field label="Your suggestion" error={errors.suggestion}>
                  <textarea
                    aria-label="Your suggestion"
                    value={improve.suggestion}
                    onChange={(e) => { setImprove({ ...improve, suggestion: e.target.value }); clearError('suggestion') }}
                    placeholder="What would you improve, and what problem would it solve for you?"
                    rows={3}
                    className={cn(textareaClass, errors.suggestion && errorBorderClass)}
                    style={inputStyle}
                  />
                </Field>
              </>
            ) : (
              <>
                <Field label="Which area (optional)">
                  <Select
                    value={newFeature.area}
                    onChange={(v) => setNewFeature({ ...newFeature, area: v as FeedbackModule })}
                    options={MODULE_OPTIONS}
                    ariaLabel="Which area"
                    className="w-full"
                  />
                </Field>
                <Field label="The idea" error={errors.idea}>
                  <textarea
                    aria-label="The idea"
                    value={newFeature.idea}
                    onChange={(e) => { setNewFeature({ ...newFeature, idea: e.target.value }); clearError('idea') }}
                    placeholder="What should Beevelope do that it doesn't today?"
                    rows={3}
                    className={cn(textareaClass, errors.idea && errorBorderClass)}
                    style={inputStyle}
                  />
                </Field>
                <Field label="Problem it solves (optional)">
                  <textarea
                    aria-label="Problem it solves"
                    value={newFeature.problem_solved}
                    onChange={(e) => setNewFeature({ ...newFeature, problem_solved: e.target.value })}
                    rows={2}
                    className={textareaClass}
                    style={inputStyle}
                  />
                </Field>
                <Field label="How important (optional)">
                  <Select
                    value={newFeature.importance}
                    onChange={(v) => setNewFeature({ ...newFeature, importance: v as FeatureImportance })}
                    options={IMPORTANCE_OPTIONS}
                    ariaLabel="How important"
                    className="w-full"
                  />
                </Field>
              </>
            )}
            <AttachmentField
              attachment={attachment} isDragging={isDragging} error={errors.attachment}
              fileInputRef={fileInputRef}
              onDrop={handleDrop}
              onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true) }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false) }}
              onFileSelect={handleFileSelect}
              onRemove={() => setAttachment(null)}
            />
          </>
        )}

        {type === 'rating' && (
          <div className="text-center py-4">
            <p className="text-foreground dark:text-[#f7f8f8] mb-3" style={{ fontSize: '14px', fontWeight: 510 }}>
              How would you rate Beevelope?
            </p>
            <div className="flex justify-center gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = n <= (hoveredStar || rating.rating)
                return (
                  <button
                    key={n}
                    type="button"
                    aria-label={`Rate ${n} star${n === 1 ? '' : 's'}`}
                    onMouseEnter={() => setHoveredStar(n)}
                    onMouseLeave={() => setHoveredStar(0)}
                    onClick={() => { setRating({ ...rating, rating: n }); clearError('rating') }}
                    className="p-0.5"
                  >
                    <Star
                      className={cn('h-7 w-7', filled ? 'text-amber-500' : 'text-muted-foreground/40 dark:text-white/[0.14]')}
                      weight={filled ? 'fill' : 'regular'}
                    />
                  </button>
                )
              })}
            </div>
            {(hoveredStar || rating.rating) > 0 && (
              <p className="mt-2 text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px', fontWeight: 590 }}>
                {RATING_LABELS[hoveredStar || rating.rating]}
              </p>
            )}
            {errors.rating && (
              <p role="alert" className="mt-2 text-red-600 dark:text-red-400" style={{ fontSize: '12px' }}>
                {errors.rating}
              </p>
            )}
            <div className="mt-5 text-left max-w-md mx-auto">
              <Field label="What's the main reason for your rating? (optional)">
                <textarea
                  aria-label="What's the main reason for your rating?"
                  value={rating.reason}
                  onChange={(e) => setRating({ ...rating, reason: e.target.value })}
                  placeholder="Tell us what you love, or what would make it a 5."
                  rows={2}
                  className={textareaClass}
                  style={inputStyle}
                />
              </Field>
            </div>
          </div>
        )}

        {submitError && (
          <p role="alert" className="text-red-600 dark:text-red-400" style={bannerTextStyle}>
            {submitError}
          </p>
        )}

        <div className="flex items-center justify-between pt-2">
          <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px' }}>
            We'll reply to {email}
          </p>
          <Button
            variant="default"
            onClick={handleSubmit}
            loading={submitting}
            disabled={type === 'rating' && rating.rating < 1}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
      )}
    </div>
  )
}

const Header: React.FC = () => (
  <div>
    <h2 className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}>
      Feedback &amp; support
    </h2>
    <p className="text-muted-foreground dark:text-[#8a8f98] mt-1" style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}>
      Report a bug, share feedback, or rate the app. We read everything.
    </p>
  </div>
)

const TypeCard: React.FC<{ label: string; icon: Icon; active: boolean; onClick: () => void; badge?: number }> = ({
  label, icon: Icon, active, onClick, badge = 0,
}) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={cn(
      'relative flex flex-col items-center gap-2 rounded-lg border p-4 transition-colors duration-150',
      active
        ? 'border-[#5e6ad2]/40 bg-[#5e6ad2]/[0.06] dark:border-[#5e6ad2]/50 dark:bg-[#5e6ad2]/[0.10]'
        : 'border-border/60 hover:border-border/80 hover:bg-muted/40 dark:border-white/[0.08] dark:hover:border-white/[0.14] dark:hover:bg-white/[0.04]',
    )}
  >
    {badge > 0 && (
      <span
        aria-label={`${badge} update${badge === 1 ? '' : 's'} from the support team`}
        className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#5e6ad2] text-white inline-flex items-center justify-center tabular-nums"
        style={{ fontSize: '11px', fontWeight: 590 }}
      >
        {badge > 99 ? '99+' : badge}
      </span>
    )}
    <Icon className={cn('h-5 w-5', active ? 'text-[#5e6ad2] dark:text-[#a5b4fc]' : 'text-muted-foreground dark:text-[#8a8f98]')} weight={active ? 'fill' : 'regular'} />
    <span
      className={active ? 'text-[#5e6ad2] dark:text-[#a5b4fc]' : 'text-foreground dark:text-[#f7f8f8]'}
      style={{ fontSize: '13px', fontWeight: 590 }}
    >
      {label}
    </span>
  </button>
)

const PillToggle: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={cn(
      'px-3 h-9 rounded-md border transition-colors duration-150',
      active
        ? 'border-[#5e6ad2]/40 bg-[#5e6ad2]/[0.08] text-[#5e6ad2] dark:border-[#5e6ad2]/50 dark:bg-[#5e6ad2]/[0.12] dark:text-[#a5b4fc]'
        : 'border-border/60 text-muted-foreground hover:border-border/80 hover:bg-muted/40 dark:border-white/[0.08] dark:text-[#8a8f98] dark:hover:border-white/[0.14] dark:hover:bg-white/[0.04]',
    )}
    style={inputStyle}
  >
    {label}
  </button>
)

const Field: React.FC<{ label: string; error?: string; children: React.ReactNode }> = ({ label, error, children }) => (
  <div>
    <label className={labelClass} style={labelStyle}>{label}</label>
    {children}
    {error && (
      <p role="alert" className="mt-1.5 text-red-600 dark:text-red-400" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
        {error}
      </p>
    )}
  </div>
)

const AttachmentField: React.FC<{
  attachment: File | null
  isDragging: boolean
  error?: string
  fileInputRef: React.RefObject<HTMLInputElement>
  onDrop: (e: React.DragEvent) => void
  onDragEnter: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDragLeave: (e: React.DragEvent) => void
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRemove: () => void
}> = ({ attachment, isDragging, error, fileInputRef, onDrop, onDragEnter, onDragOver, onDragLeave, onFileSelect, onRemove }) => (
  <div>
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={() => !attachment && fileInputRef.current?.click()}
      className={cn(
        'border-2 border-dashed rounded-lg p-4 text-center transition-colors duration-150 cursor-pointer',
        isDragging
          ? 'border-[#7170ff]/60 bg-[#5e6ad2]/[0.05] dark:bg-[#5e6ad2]/[0.08]'
          : 'border-border/60 hover:border-border/80 hover:bg-muted/30 dark:border-white/[0.08] dark:hover:border-white/[0.14] dark:hover:bg-white/[0.02]',
      )}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".png,.jpg,.jpeg,.pdf"
        onChange={onFileSelect}
      />
      {attachment ? (
        <div className="flex items-center justify-center gap-2 text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px' }}>
          <span className="truncate max-w-[14rem]">{attachment.name}</span>
          <button
            type="button"
            aria-label="Remove attachment"
            onClick={(e) => { e.stopPropagation(); onRemove() }}
            className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8]"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <p className="text-muted-foreground dark:text-[#8a8f98] flex items-center justify-center gap-2" style={{ fontSize: '13px' }}>
          <CloudArrowUp className="h-4 w-4" />
          Attach a screenshot or file
        </p>
      )}
    </div>
    {error && (
      <p role="alert" className="mt-1.5 text-red-600 dark:text-red-400" style={{ fontSize: '12px' }}>
        {error}
      </p>
    )}
  </div>
)

export default FeedbackSupportSection
